import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import SourcesPanel from "@/components/SourcesPanel";
import { toast } from "sonner";
import {
    Sparkles, Loader2, FlaskConical, Building2, Target, AlertTriangle,
    CheckCircle2, Calendar, BookOpen, Users, Mic, GraduationCap,
    ClipboardList, RefreshCw, TrendingUp, TrendingDown, Minus, Clock, Trash2,
} from "lucide-react";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { formatGbp, formatRelative } from "@/lib/format";

const PRODUCT_ICONS = {
    "Nursing Live": Users,
    "RCNi Journal Advertising": BookOpen,
    "Webinar Sponsorship": Mic,
    "Podcast Sponsorship": Mic,
    "RCN Accreditation": GraduationCap,
    "Surveys": ClipboardList,
};

function SpendBlock({ amount, color = "#1A4331" }) {
    return (
        <div className="px-5 py-4 rounded-lg" style={{ backgroundColor: color + "10", border: `1px solid ${color}30` }}>
            <p className="font-heading text-3xl font-semibold leading-none" style={{ color }} data-testid="spend-amount">
                {formatGbp(amount)}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-[#8A9A86] mt-2">Estimated annual spend</p>
        </div>
    );
}

function labelColor(label) {
    switch (label) {
        case "Very High": return "#991B1B";
        case "High": return "#C0604A";
        case "Moderate": return "#B45309";
        case "Low": return "#8A9A86";
        default: return "#1A4331";
    }
}

function TrendBadge({ trend, color = "#1A4331" }) {
    const map = {
        rising: { Icon: TrendingUp, text: "Rising", bg: "#DCFCE7", fg: "#166534" },
        declining: { Icon: TrendingDown, text: "Declining", bg: "#FEE2E2", fg: "#991B1B" },
        stable: { Icon: Minus, text: "Stable", bg: "#F1F5F9", fg: "#475569" },
    };
    const m = map[trend] || map.stable;
    const Icon = m.Icon;
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium" style={{ backgroundColor: m.bg, color: m.fg }}>
            <Icon className="w-3 h-3" /> {m.text}
        </span>
    );
}

export default function DrugDetailSheet({ drugId, onClose, onChanged }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [drug, setDrug] = useState(null);
    const [intel, setIntel] = useState(null);
    const [salespeople, setSalespeople] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [assignee, setAssignee] = useState("unassigned");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const open = !!drugId;

    useEffect(() => {
        if (!drugId) return;
        setLoading(true);
        Promise.all([
            api.get(`/drugs/${drugId}`),
            api.get("/users/salespeople"),
        ])
            .then(([dr, sp]) => {
                setDrug(dr.data);
                setIntel(dr.data.intelligence);
                setSalespeople(sp.data);
                setAssignee(dr.data.assignment?.salesperson_id || "unassigned");
                setNotes(dr.data.assignment?.notes || "");
            })
            .catch(() => toast.error("Failed to load drug"))
            .finally(() => setLoading(false));
    }, [drugId]);

    const generate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post(`/drugs/${drugId}/intelligence`);
            setIntel(data);
            toast.success("Intelligence generated");
            onChanged && onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI generation failed");
        } finally { setGenerating(false); }
    };

    const saveAssignment = async () => {
        setSaving(true);
        try {
            const payload = {
                salesperson_id: assignee === "unassigned" ? null : assignee,
                notes,
            };
            await api.post(`/drugs/${drugId}/assign`, payload);
            toast.success(assignee === "unassigned" ? "Assignment cleared" : "Assignment saved");
            onChanged && onChanged();
        } catch (e) {
            toast.error("Failed to save assignment");
        } finally { setSaving(false); }
    };

    const deleteDrug = async () => {
        if (!window.confirm(`Permanently delete "${drug.name}"? This removes its intel and assignment.`)) return;
        setDeleting(true);
        try {
            await api.delete(`/drugs/${drugId}`);
            toast.success(`${drug.name} deleted`);
            onChanged && onChanged();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally { setDeleting(false); }
    };

    const close = (v) => { if (!v) onClose(); };

    return (
        <Sheet open={open} onOpenChange={close}>
            <SheetContent side="right" className="w-full sm:max-w-2xl bg-[#F8F7F4] overflow-y-auto p-0 border-l-[#E2E8F0]">
                {loading || !drug ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 animate-spin text-[#1A4331]" />
                    </div>
                ) : (
                    <div>
                        {/* Header */}
                        <SheetHeader className="bg-white border-b border-[#E2E8F0] p-6 sticky top-0 z-10">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-lg bg-[#1A4331] flex items-center justify-center shrink-0">
                                    <FlaskConical className="w-5 h-5 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <SheetTitle className="font-heading text-2xl font-semibold text-[#1A4331] leading-tight text-left" data-testid="drug-detail-name">
                                        {drug.name}
                                    </SheetTitle>
                                    {drug.generic_name && (
                                        <p className="text-sm text-[#475569] mt-0.5">{drug.generic_name}</p>
                                    )}
                                    <div className="flex items-center gap-2 flex-wrap mt-3">
                                        <Badge className="bg-[#1A4331] text-white hover:bg-[#1A4331] border-0">{drug.development_stage}</Badge>
                                        <Badge className="bg-[#F1F5F9] text-[#1E293B] hover:bg-[#F1F5F9] border-0">{drug.clinical_area}</Badge>
                                        {(drug.intelligence?.generated_at || drug.assignment?.updated_at || drug.updated_at) && (
                                            <span className="inline-flex items-center gap-1 text-xs text-[#8A9A86] ml-1" data-testid="last-updated-header">
                                                <Clock className="w-3 h-3" />
                                                Updated {formatRelative(
                                                    [drug.intelligence?.generated_at, drug.assignment?.updated_at, drug.updated_at]
                                                        .filter(Boolean)
                                                        .sort()
                                                        .pop()
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </SheetHeader>

                        <Tabs defaultValue="intel" className="p-6">
                            <TabsList className="grid grid-cols-4 bg-white border border-[#E2E8F0]">
                                <TabsTrigger value="intel" data-testid="tab-intel">AI Intelligence</TabsTrigger>
                                <TabsTrigger value="sources" data-testid="tab-sources">Sources</TabsTrigger>
                                <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                                <TabsTrigger value="assign" data-testid="tab-assign">Assign</TabsTrigger>
                            </TabsList>

                            {/* ===== AI Intelligence ===== */}
                            <TabsContent value="intel" className="mt-6 space-y-6">
                                {!intel ? (
                                    <div className="rounded-xl border border-[#1A4331]/30 bg-white p-8 text-center">
                                        <div className="w-14 h-14 rounded-full bg-[#1A4331]/10 mx-auto flex items-center justify-center">
                                            <Sparkles className="w-6 h-6 text-[#1A4331]" />
                                        </div>
                                        <h3 className="font-heading text-xl font-medium text-[#1A4331] mt-5">
                                            Generate spend likelihood
                                        </h3>
                                        <p className="text-sm text-[#475569] mt-2 max-w-sm mx-auto leading-relaxed">
                                            Claude Sonnet 4.5 will analyse {drug.company}&apos;s spending trend, the clinical area,
                                            launch proximity and ABPI constraints to produce evidence-backed scores.
                                        </p>
                                        <Button
                                            onClick={generate}
                                            disabled={generating}
                                            className="mt-6 bg-[#1A4331] hover:bg-[#123022] text-white gap-2"
                                            data-testid="generate-intel-btn"
                                        >
                                            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                            {generating ? "Analysing…" : "Generate intelligence"}
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-6" data-testid="intel-panel">
                                        {/* Summary */}
                                        <div className="rounded-xl bg-gradient-to-br from-[#1A4331] to-[#123022] p-6 text-white">
                                            <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-widest">
                                                <Sparkles className="w-3.5 h-3.5" /> Executive summary
                                                <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-[#FEF3C7] text-[#92400E] normal-case tracking-normal">
                                                    <Sparkles className="w-3 h-3" /> AI estimate
                                                </span>
                                            </div>
                                            <p className="mt-3 leading-relaxed text-sm">{intel.overall_summary}</p>
                                            <p className="mt-4 text-[10px] uppercase tracking-widest text-white/50">
                                                Generated {new Date(intel.generated_at).toLocaleString("en-GB")} · See the
                                                <span className="underline decoration-white/40 mx-1">Sources</span>
                                                tab for disclosed evidence behind these figures
                                            </p>
                                        </div>

                                        {/* 3-year spend trend */}
                                        {intel.spend_trend && intel.spend_trend.length > 0 && (
                                            <div className="rounded-xl bg-white border border-[#E2E8F0] p-6" data-testid="spend-trend-chart">
                                                <div className="flex items-center gap-2 mb-4">
                                                    <TrendingUp className="w-4 h-4 text-[#1A4331]" />
                                                    <h3 className="font-heading text-lg font-medium text-[#1A4331]">3-year spend trend — {drug.clinical_area}</h3>
                                                </div>
                                                <p className="text-xs text-[#475569] mb-4 leading-relaxed">
                                                    {drug.company}&apos;s nursing-market £ spend in this clinical area — split by medical affairs and drug promotion.
                                                </p>
                                                <div className="h-56">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <LineChart data={intel.spend_trend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                                            <XAxis dataKey="year" tick={{ fill: "#475569", fontSize: 12 }} axisLine={false} tickLine={false} />
                                                            <YAxis tick={{ fill: "#475569", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatGbp(v)} />
                                                            <Tooltip
                                                                contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13 }}
                                                                formatter={(v) => formatGbp(v)}
                                                            />
                                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                                            <Line type="monotone" dataKey="medical_affairs_gbp" name="Medical Affairs" stroke="#1A4331" strokeWidth={2.5} dot={{ r: 4, fill: "#1A4331" }} />
                                                            <Line type="monotone" dataKey="promotion_gbp" name="Drug Promotion" stroke="#C0604A" strokeWidth={2.5} dot={{ r: 4, fill: "#C0604A" }} />
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        )}

                                        {/* Bento scores */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="rounded-xl bg-white border border-[#1A4331]/30 p-6">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-xs uppercase tracking-widest text-[#8A9A86]">Medical Affairs</p>
                                                    <Badge className="border-0" style={{ backgroundColor: labelColor(intel.medical_affairs_label) + "20", color: labelColor(intel.medical_affairs_label) }}>
                                                        {intel.medical_affairs_label}
                                                    </Badge>
                                                </div>
                                                <div className="mt-4 space-y-3">
                                                    <SpendBlock amount={intel.medical_affairs_spend_gbp} color="#1A4331" />
                                                    <TrendBadge trend={intel.medical_affairs_trend} />
                                                    <p className="text-xs text-[#475569] leading-relaxed">
                                                        Estimated 12-month spend by {drug.company} on medical affairs to UK nursing market.
                                                    </p>
                                                </div>
                                                <Separator className="my-4" />
                                                <p className="text-xs uppercase tracking-widest text-[#8A9A86] mb-2">Evidence</p>
                                                <ul className="space-y-2">
                                                    {intel.medical_affairs_evidence.map((b) => (
                                                        <li key={b} className="flex gap-2 text-sm text-[#1E293B] leading-relaxed">
                                                            <CheckCircle2 className="w-4 h-4 text-[#1A4331] shrink-0 mt-0.5" />
                                                            <span>{b}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>

                                            <div className="rounded-xl bg-white border border-[#C0604A]/30 p-6">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-xs uppercase tracking-widest text-[#8A9A86]">Drug Promotion</p>
                                                    <Badge className="border-0" style={{ backgroundColor: labelColor(intel.promotion_label) + "20", color: labelColor(intel.promotion_label) }}>
                                                        {intel.promotion_label}
                                                    </Badge>
                                                </div>
                                                <div className="mt-4 space-y-3">
                                                    <SpendBlock amount={intel.promotion_spend_gbp} color="#C0604A" />
                                                    <TrendBadge trend={intel.promotion_trend} color="#C0604A" />
                                                    <p className="text-xs text-[#475569] leading-relaxed">
                                                        Estimated 12-month ABPI-compliant promotional spend to UK nursing market.
                                                    </p>
                                                </div>
                                                <Separator className="my-4" />
                                                <p className="text-xs uppercase tracking-widest text-[#8A9A86] mb-2">Evidence</p>
                                                <ul className="space-y-2">
                                                    {intel.promotion_evidence.map((b) => (
                                                        <li key={b} className="flex gap-2 text-sm text-[#1E293B] leading-relaxed">
                                                            <CheckCircle2 className="w-4 h-4 text-[#C0604A] shrink-0 mt-0.5" />
                                                            <span>{b}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>

                                        {/* RCN product recs */}
                                        <div>
                                            <div className="flex items-center gap-2 mb-3">
                                                <Target className="w-4 h-4 text-[#1A4331]" />
                                                <h3 className="font-heading text-lg font-medium text-[#1A4331]">RCN product recommendations</h3>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="rcn-recommendations">
                                                {intel.rcn_product_recommendations.map((r) => {
                                                    const Icon = PRODUCT_ICONS[r.product] || Target;
                                                    return (
                                                        <div key={r.product} className="rounded-lg bg-white border border-[#E2E8F0] p-4 card-lift">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="flex items-center gap-2">
                                                                    <Icon className="w-4 h-4 text-[#1A4331]" />
                                                                    <p className="font-medium text-sm text-[#1E293B]">{r.product}</p>
                                                                </div>
                                                                <span className="font-mono text-xs font-semibold text-[#1A4331]">{r.fit_score}</span>
                                                            </div>
                                                            {!r.abpi_compliant_for_this_drug && (
                                                                <Badge className="mt-2 bg-[#FEF3C7] text-[#92400E] hover:bg-[#FEF3C7] border-0 gap-1">
                                                                    <AlertTriangle className="w-3 h-3" /> Medical Affairs only
                                                                </Badge>
                                                            )}
                                                            <p className="text-xs text-[#475569] mt-2 leading-relaxed">{r.rationale}</p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <Button
                                            variant="outline"
                                            onClick={generate}
                                            disabled={generating}
                                            className="w-full gap-2 border-[#1A4331] text-[#1A4331] hover:bg-[#1A4331] hover:text-white"
                                            data-testid="regenerate-intel-btn"
                                        >
                                            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                            Regenerate analysis
                                        </Button>
                                    </div>
                                )}
                            </TabsContent>

                            {/* ===== Sources & methodology ===== */}
                            <TabsContent value="sources" className="mt-6">
                                <SourcesPanel drugId={drugId} />
                            </TabsContent>

                            {/* ===== Details ===== */}
                            <TabsContent value="details" className="mt-6 space-y-4">
                                <div className="bg-white rounded-lg border border-[#E2E8F0] p-5 space-y-3 text-sm">
                                    <Field icon={Building2} label="Company">{drug.company}</Field>
                                    <Field icon={Building2} label="Patent Holder">{drug.patent_holder || drug.company}</Field>
                                    <Field icon={FlaskConical} label="Mechanism">{drug.mechanism || "—"}</Field>
                                    <Field icon={Target} label="Indication">{drug.indication}</Field>
                                    <Field icon={CheckCircle2} label="UK Status">{drug.uk_status}</Field>
                                    <Field icon={Calendar} label="Expected Launch">{drug.expected_launch || "—"}</Field>
                                </div>
                                {drug.sources?.length > 0 && (
                                    <div className="bg-white rounded-lg border border-[#E2E8F0] p-5">
                                        <p className="text-xs uppercase tracking-widest text-[#8A9A86] mb-2">Sources</p>
                                        <div className="flex flex-wrap gap-2">
                                            {drug.sources.map((s) => (
                                                <Badge key={s} className="bg-[#F1F5F9] text-[#1E293B] hover:bg-[#F1F5F9] border-0">{s}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {isAdmin && (
                                    <div className="bg-white rounded-lg border border-[#FECACA] p-5">
                                        <p className="text-xs uppercase tracking-widest text-[#991B1B] mb-2">Danger zone</p>
                                        <p className="text-xs text-[#475569] mb-3 leading-relaxed">
                                            Removes the drug and all its intelligence + assignment data. Use this to prune auto-discovered drugs you don&apos;t want in the catalogue.
                                        </p>
                                        <Button
                                            variant="outline"
                                            onClick={deleteDrug}
                                            disabled={deleting}
                                            className="gap-2 border-[#FECACA] text-[#991B1B] hover:bg-[#FEE2E2] hover:text-[#991B1B]"
                                            data-testid="delete-drug-btn"
                                        >
                                            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                            Delete drug
                                        </Button>
                                    </div>
                                )}
                            </TabsContent>

                            {/* ===== Assign ===== */}
                            <TabsContent value="assign" className="mt-6 space-y-4">
                                <div className="bg-white rounded-lg border border-[#E2E8F0] p-5 space-y-5">
                                    <div>
                                        <label className="text-xs uppercase tracking-widest text-[#8A9A86]">Assign to salesperson</label>
                                        <Select value={assignee} onValueChange={setAssignee}>
                                            <SelectTrigger className="mt-2 h-11 bg-white border-[#E2E8F0]" data-testid="assign-select">
                                                <SelectValue placeholder="Select salesperson" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="unassigned">Unassigned</SelectItem>
                                                {salespeople.map((p) => (
                                                    <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <label className="text-xs uppercase tracking-widest text-[#8A9A86]">Notes</label>
                                        <Textarea
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            placeholder="Opportunity rationale, next steps, contact history…"
                                            className="mt-2 min-h-[120px] bg-white border-[#E2E8F0]"
                                            data-testid="assign-notes"
                                        />
                                    </div>
                                    <Button
                                        onClick={saveAssignment}
                                        disabled={saving}
                                        className="w-full bg-[#1A4331] hover:bg-[#123022] text-white"
                                        data-testid="save-assignment-btn"
                                    >
                                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                        Save assignment
                                    </Button>
                                </div>
                                {drug.assignment && (
                                    <p className="text-xs text-[#475569] text-center">
                                        Last updated {new Date(drug.assignment.updated_at).toLocaleString("en-GB")} by {drug.assignment.updated_by}
                                    </p>
                                )}
                            </TabsContent>
                        </Tabs>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

function Field({ icon: Icon, label, children }) {
    return (
        <div className="flex items-start gap-3">
            <Icon className="w-4 h-4 text-[#8A9A86] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-[#8A9A86]">{label}</p>
                <p className="text-[#1E293B] mt-0.5">{children}</p>
            </div>
        </div>
    );
}
